import { type Primitive } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { type ReactNode } from 'react';

import { GenericErrorBanner } from '../GenericErrorBanner';

interface ErrorBoundaryProps {
  /**
   * The application that the error boundary is for.
   */
  app?: 'bo' | 'fo' | 'po';
  /**
   * The children of the error boundary.
   */
  children: ReactNode;
  /**
   * The context of the error boundary.
   */
  context?: {
    tags?: { [key: string]: Primitive };
  };
  /**
   * The module that the error boundary is for.
   */
  module?: string;
  /**
   * Whether to reload the page when resetting the error.
   * @default false
   */
  shouldReloadPageWhenResettingError?: boolean;
  /**
   * The module that the error boundary is for.
   */
  submodule?: string;
  /**
   * The team that owns the application based on the CODEOWNERS file.
   */
  team?: 'squad-admin-xp' | 'squad-evolution-frontend' | 'squad-uj';
}

const ErrorBoundary = ({
  children,
  context,
  team,
  app,
  module,
  shouldReloadPageWhenResettingError = false,
  submodule,
}: ErrorBoundaryProps) => (
  <Sentry.ErrorBoundary
    beforeCapture={(scope) => {
      if (context?.tags) {
        scope.setTags(context.tags);
      }

      if (team) {
        scope.setTag('team', team);
      }

      if (app) {
        scope.setTag('app', app);
      }

      if (module) {
        scope.setTag('module', module);
      }

      if (submodule) {
        scope.setTag('submodule', submodule);
      }

      scope.setTag('page_locale', I18n.locale);
    }}
    fallback={(props) => (
      <GenericErrorBanner
        theme={app}
        resetError={() => {
          if (shouldReloadPageWhenResettingError) {
            window.location.reload();
          } else {
            props.resetError();
          }
        }}
      />
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default ErrorBoundary;

type TeamErrorBoundary = Omit<ErrorBoundaryProps, 'team'>;

export const AdminXpTeamErrorBoundary = (props: TeamErrorBoundary) => (
  <ErrorBoundary {...props} team="squad-admin-xp" />
);

export const EvolutionTeamErrorBoundary = (props: TeamErrorBoundary) => (
  <ErrorBoundary {...props} team="squad-evolution-frontend" />
);

export const UjTeamErrorBoundary = (props: TeamErrorBoundary) => (
  <ErrorBoundary {...props} team="squad-uj" />
);

export const BuilderErrorBoundary = (props: TeamErrorBoundary) => (
  <AdminXpTeamErrorBoundary {...props} module="builder" />
);
